import '../../i18n/autoTranslate';
import React, { useMemo, useState } from "react";
import SpotlightCard from "../../../components/ui/SpotlightCard";
import Icon from "../../../components/AppIcon";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const pad2 = (n) => String(n).padStart(2, "0");

const dateKeyLocal = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const formatLong = (d) =>
  d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const monthShort = (d) => d.toLocaleDateString("en-US", { month: "short" });

const RANGES = [
  { key: "12m", label: "12M", days: 365 },
  { key: "6m", label: "6M", days: 183 },
  { key: "3m", label: "3M", days: 91 },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * GitHub-style heatmap for completed interviews.
 * Now with: range selector, fixed month labels, fixed legend bucket coloring,
 * tooltip that hides score on 0-session days, and "best day of week" insight.
 */
const HeatMapVisualization = ({ interviews = [] }) => {
  const [rangeKey, setRangeKey] = useState("12m");
  const DAYS = RANGES.find((r) => r.key === rangeKey)?.days ?? 365;

  const computed = useMemo(() => {
    const now = startOfDay(new Date());
    const rangeStart = startOfDay(addDays(now, -(DAYS - 1)));

    // Daily map for completed interviews
    const completed = (interviews || []).filter(
      (i) =>
        String(i?.status || "").toLowerCase() === "completed" &&
        (i?.completed_at || i?.created_at)
    );

    const byDay = new Map();
    for (const it of completed) {
      const d = new Date(it.completed_at || it.created_at);
      const key = dateKeyLocal(d);
      if (!byDay.has(key)) {
        byDay.set(key, { sessions: 0, sumScore: 0, scoreCount: 0 });
      }
      const obj = byDay.get(key);
      obj.sessions += 1;

      const score = safeNum(
        it.performance_score ?? it.overall_score ?? it.final_score ?? it.score,
        NaN
      );
      if (Number.isFinite(score)) {
        obj.sumScore += score;
        obj.scoreCount += 1;
      }
    }

    // Align grid to whole weeks (Sunday → Saturday)
    const alignedStart = addDays(rangeStart, -rangeStart.getDay());
    const alignedEnd = addDays(now, 6 - now.getDay());

    const days = [];
    for (let d = new Date(alignedStart); d <= alignedEnd; d = addDays(d, 1)) {
      const inRange = d >= rangeStart && d <= now;
      const isFuture = d > now;

      let sessions = null;
      let avg = null;

      if (inRange) {
        const key = dateKeyLocal(d);
        const found = byDay.get(key);
        sessions = found?.sessions ?? 0;
        avg =
          found && found.scoreCount
            ? Math.round((found.sumScore / found.scoreCount) * 10) / 10
            : null;
      }

      days.push({
        date: new Date(d),
        key: dateKeyLocal(d),
        inRange,
        isFuture,
        sessions,
        avg,
      });
    }

    const weeksCount = Math.ceil(days.length / 7);

    // FIX: month labels were dropping the first visible week of a month.
    // The original code updated `prevMonth = m` even when `shouldShow=false`,
    // so the next iteration thought we were "still in the same month" and
    // skipped that label. We now only advance prevMonth when we actually
    // RENDER a label, so the first visible week of every month gets one.
    const monthLabels = Array(weeksCount).fill("");
    let lastShownMonth = null;
    for (let w = 0; w < weeksCount; w++) {
      const sunday = days[w * 7]?.date;
      if (!sunday) continue;
      // Only consider weeks that overlap the actual range
      if (sunday < addDays(rangeStart, -6)) continue;

      const m = sunday.getMonth();
      if (m !== lastShownMonth) {
        monthLabels[w] = monthShort(sunday);
        lastShownMonth = m;
      }
    }

    // Stats
    const inRangeDays = days.filter((x) => x.inRange);
    const totalSessions = inRangeDays.reduce(
      (s, x) => s + (x.sessions ?? 0),
      0
    );
    const activeDays = inRangeDays.reduce(
      (s, x) => s + ((x.sessions ?? 0) > 0 ? 1 : 0),
      0
    );

    // streaks (consecutive days with sessions > 0 within range)
    let bestStreak = 0;
    let running = 0;
    for (const x of inRangeDays) {
      if ((x.sessions ?? 0) > 0) {
        running += 1;
        bestStreak = Math.max(bestStreak, running);
      } else {
        running = 0;
      }
    }
    let curStreak = 0;
    for (let i = inRangeDays.length - 1; i >= 0; i--) {
      const x = inRangeDays[i];
      if ((x.sessions ?? 0) > 0) curStreak += 1;
      else break;
    }

    // Best-day-of-week insight: which weekday averages the most sessions?
    const dowBuckets = Array.from({ length: 7 }, () => ({
      sessions: 0,
      days: 0,
    }));
    for (const x of inRangeDays) {
      const dow = x.date.getDay();
      dowBuckets[dow].days += 1;
      dowBuckets[dow].sessions += x.sessions ?? 0;
    }
    const dowStats = dowBuckets.map((b, i) => ({
      day: DAY_NAMES[i],
      avgPerDay: b.days > 0 ? b.sessions / b.days : 0,
      total: b.sessions,
    }));
    const bestDow = [...dowStats].sort(
      (a, b) => b.avgPerDay - a.avgPerDay
    )[0];

    return {
      days,
      weeksCount,
      monthLabels,
      totalSessions,
      activeDays,
      curStreak,
      bestStreak,
      rangeStart,
      now,
      bestDow,
      activePct: inRangeDays.length
        ? Math.round((activeDays / inRangeDays.length) * 100)
        : 0,
    };
  }, [interviews, DAYS]);

  // FIX: The legend used to rely on colorClass(min, true, false) and
  // colorClass treats 0 specially. So the "0" legend swatch came out as the
  // empty-day color (almost invisible against the dark surface) and "4+" was
  // identical to "3" because both fell into the same bucket in the original
  // class function. We define explicit legend swatches that match the cell
  // levels exactly.
  const LEVEL_CLASSES = [
    "bg-white/[0.05] border-white/10", // 0
    "bg-violet-400/20 border-violet-300/25", // 1
    "bg-sky-400/24 border-sky-300/30", // 2
    "bg-emerald-400/28 border-emerald-300/34", // 3
    "bg-emerald-200/40 border-emerald-100/50", // 4+
  ];
  const LEVEL_LABELS = ["0", "1", "2", "3", "4+"];

  const colorClass = (sessions, inRange, isFuture) => {
    if (!inRange || isFuture || sessions == null) {
      return "bg-white/[0.02] border-white/5 opacity-55";
    }
    const lvl = Math.min(4, sessions);
    return LEVEL_CLASSES[lvl];
  };

  const dayLabels = [
    { idx: 1, text: "Mon" },
    { idx: 3, text: "Wed" },
    { idx: 5, text: "Fri" },
  ];

  const CELL = 11;
  const GAP = 3;

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(56,189,248,0.12)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-24 h-[340px] w-[340px] rounded-full bg-violet-400/10 blur-[90px]" />
        <div className="absolute -bottom-24 -left-24 h-[340px] w-[340px] rounded-full bg-emerald-400/10 blur-[90px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/5 to-black/45" />
      </div>

      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
              <Icon name="Activity" size={18} className="text-white/80" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">
                Practice heatmap
              </div>
              <div className="text-xs text-white/55 mt-1">
                Consistency view — completed interviews per day.
              </div>
            </div>
          </div>

          {/* Range selector */}
          <div className="inline-flex items-center rounded-2xl border border-white/10 bg-white/[0.03] p-1">
            {RANGES.map((r) => {
              const active = rangeKey === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRangeKey(r.key)}
                  className={[
                    "px-3 py-1.5 text-xs font-semibold rounded-xl transition",
                    active
                      ? "bg-white/10 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.10)]"
                      : "text-white/60 hover:text-white/85 hover:bg-white/[0.06]",
                  ].join(" ")}
                  aria-pressed={active}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2">
            <div className="text-[11px] text-white/55">Interviews</div>
            <div className="text-sm font-semibold text-white tabular-nums">
              {computed.totalSessions}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2">
            <div className="text-[11px] text-white/55">Active days</div>
            <div className="text-sm font-semibold text-white tabular-nums">
              {computed.activeDays}{" "}
              <span className="text-white/45 text-[11px]">
                ({computed.activePct}%)
              </span>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2">
            <div className="text-[11px] text-white/55">Current streak</div>
            <div className="text-sm font-semibold text-white tabular-nums">
              {computed.curStreak}d
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2">
            <div className="text-[11px] text-white/55">Best streak</div>
            <div className="text-sm font-semibold text-white tabular-nums">
              {computed.bestStreak}d
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2">
            <div className="text-[11px] text-white/55">Top day</div>
            <div className="text-sm font-semibold text-white">
              {computed.bestDow && computed.bestDow.total > 0
                ? computed.bestDow.day
                : "—"}
            </div>
          </div>
        </div>

        {/* Heatmap */}
        <div className="mt-6 rounded-3xl border border-white/10 bg-black/20 p-4 backdrop-blur-xl">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-max">
              {/* Month labels row */}
              <div
                className="ml-10 grid"
                style={{
                  gridTemplateColumns: `repeat(${computed.weeksCount}, ${CELL}px)`,
                  columnGap: `${GAP}px`,
                }}
              >
                {computed.monthLabels.map((m, i) => (
                  <div
                    key={i}
                    className="h-4 text-[10px] text-white/45 leading-4"
                  >
                    {m}
                  </div>
                ))}
              </div>

              <div className="flex">
                {/* Day labels */}
                <div
                  className="mr-3 grid"
                  style={{
                    gridTemplateRows: `repeat(7, ${CELL}px)`,
                    rowGap: `${GAP}px`,
                    width: 34,
                    paddingTop: 0,
                  }}
                >
                  {Array.from({ length: 7 }).map((_, r) => {
                    const label =
                      dayLabels.find((x) => x.idx === r)?.text || "";
                    return (
                      <div
                        key={r}
                        className="text-[10px] text-white/40 flex items-center justify-end"
                        style={{ height: CELL }}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>

                {/* Cells (column-major) */}
                <div
                  className="grid"
                  style={{
                    gridAutoFlow: "column",
                    gridTemplateRows: `repeat(7, ${CELL}px)`,
                    gap: `${GAP}px`,
                  }}
                >
                  {computed.days.map((x) => {
                    const sessions = x.sessions ?? 0;

                    const tooltip = (
                      <div className="text-xs">
                        <div className="font-semibold text-white">
                          {formatLong(x.date)}
                        </div>
                        {x.inRange && !x.isFuture ? (
                          sessions > 0 ? (
                            <>
                              <div className="mt-1 text-white/75">
                                Interviews:{" "}
                                <span className="text-white font-semibold">
                                  {sessions}
                                </span>
                              </div>
                              {x.avg != null ? (
                                <div className="text-white/75">
                                  Avg score:{" "}
                                  <span className="text-white font-semibold">
                                    {x.avg}
                                  </span>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="mt-1 text-white/55">No sessions</div>
                          )
                        ) : (
                          <div className="mt-1 text-white/55">
                            {x.isFuture ? "Future day" : "Outside range"}
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <Tippy
                        key={x.key}
                        content={tooltip}
                        appendTo={() => document.body}
                        zIndex={999999}
                        placement="top"
                        delay={[60, 0]}
                        className="rounded-2xl border border-white/10 bg-[#080a0f]/95 px-3 py-2 shadow-2xl backdrop-blur-xl"
                      >
                        <div
                          aria-label={`${formatLong(x.date)}: ${sessions} session${sessions === 1 ? "" : "s"}`}
                          className={[
                            "rounded-[3px] border transition",
                            "hover:brightness-125",
                            "cursor-default",
                            colorClass(sessions, x.inRange, x.isFuture),
                          ].join(" ")}
                          style={{ width: CELL, height: CELL }}
                        />
                      </Tippy>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-white/55">
              {computed.rangeStart.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              →{" "}
              {computed.now.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>

            <div className="flex items-center gap-2 text-[11px] text-white/55">
              <span className="mr-1">Less</span>
              {LEVEL_CLASSES.map((cls, i) => (
                <span
                  key={i}
                  title={LEVEL_LABELS[i]}
                  className={["inline-block rounded-[3px] border", cls].join(
                    " "
                  )}
                  style={{ width: CELL, height: CELL }}
                  aria-label={`${LEVEL_LABELS[i]} sessions`}
                />
              ))}
              <span className="ml-1">More</span>
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default HeatMapVisualization;
